import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { markRivhitDocumentsDirty, rememberRivhitDocumentLink } from '../lib/rivhitDocumentsSync';
import type { UserUsageFeature } from '../lib/userUsage';
import type {
  ReportCustomerPreset,
  ReportCustomerPresetInput,
} from '../types/reportCustomerPresets';
export type {
  ReportCustomerPreset,
  ReportCustomerPresetFilters,
  ReportCustomerPresetGrowthStatus,
  ReportCustomerPresetInput,
} from '../types/reportCustomerPresets';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

export interface ExpensePeriod {
  from_date: string;
  to_date: string;
}

export interface ExpenseFilters extends ExpensePeriod {
  category?: string;
  supplier?: string;
  search?: string;
}

export type ExpenseAmount = string | number;
export interface ExpenseTotals {
  accounting_amount: ExpenseAmount;
  vat_amount: ExpenseAmount | null;
  gross_amount: ExpenseAmount | null;
  transaction_count: number;
  vat_known_count: number;
}
export interface ExpenseGroup extends ExpenseTotals {
  id: string;
  name: string | null;
  previous_amount: ExpenseAmount;
  change_percent: ExpenseAmount | null;
}
export interface ExpenseJob extends ExpensePeriod {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  phase: 'accounts' | 'journals' | 'complete';
  completed_units: number;
  total_units: number;
  error_code: string | null;
  updated_at: string;
}
export interface ExpenseReport extends ExpensePeriod {
  date_basis: 'accounting_month';
  status: 'ready' | 'no_data' | 'not_configured' | 'unsupported_provider';
  previous_from_date: string;
  previous_to_date: string;
  snapshot_id: string | null;
  generated_at: string | null;
  totals: ExpenseTotals | null;
  previous_totals: ExpenseTotals | null;
  change_percent: ExpenseAmount | null;
  categories: ExpenseGroup[];
  suppliers: ExpenseGroup[];
  months: (ExpenseTotals & { month: string; previous_amount: ExpenseAmount; is_partial: boolean; categories: ExpenseGroup[] })[];
  category_options: { id: string; name: string | null }[];
  supplier_options: { id: string; name: string | null }[];
  active_job: ExpenseJob | null;
  warnings: string[];
}
export interface ExpenseTransaction {
  id: string;
  journal_key: string;
  accounting_date: string;
  reference_date: string;
  accounting_month: string;
  category_name: string;
  category_id: string;
  account_id: number;
  supplier_name: string | null;
  supplier_id: number | null;
  description: string;
  reference1: string;
  reference2: string;
  accounting_amount: ExpenseAmount;
  vat_amount: ExpenseAmount | null;
  gross_amount: ExpenseAmount | null;
  currency: 'ILS';
  sort_code: number | null;
}
export interface ExpenseTransactionPage {
  snapshot_id: string;
  transactions: ExpenseTransaction[];
  next_cursor: string | null;
  total: number;
}
export const getExpenses = (params: ExpenseFilters, signal?: AbortSignal) =>
  api.get<ExpenseReport>('/expenses', { params, signal }).then((response) => response.data);
export const syncExpenses = (period: ExpensePeriod, signal?: AbortSignal) =>
  api.post<ExpenseJob>('/expenses/sync', period, { signal }).then((response) => response.data);
export const getExpenseJob = (id: string, signal?: AbortSignal) =>
  api.get<ExpenseJob>(`/expenses/sync/${encodeURIComponent(id)}`, { signal }).then((response) => response.data);
export const getExpenseTransactions = (
  params: ExpenseFilters & { snapshot_id: string; cursor?: string; sort: 'date' | 'amount'; direction: 'asc' | 'desc' },
  signal?: AbortSignal,
) => api.get<ExpenseTransactionPage>('/expenses/transactions', { params, signal }).then((response) => response.data);

export interface McpConsent {
  client_name: string;
  workspaces: { tenant_id: string; name: string }[];
}

export const MCP_LEGACY_AUTHORIZE_URL = `${API_BASE_URL}/mcp/oauth/authorize`;

export async function getMcpConsent(continuation: string): Promise<McpConsent> {
  const { data } = await api.get<McpConsent>('/mcp/oauth/resume', { params: { continuation } });
  return data;
}

export async function submitMcpConsent(continuation: string, tenantId: string, approve: boolean) {
  const { data } = await api.post<{ redirect_to: string }>('/mcp/oauth/resume', {
    continuation, tenant_id: tenantId, approve,
  });
  return data.redirect_to;
}

// Token storage — in-memory only. The server sets an HttpOnly `auth_token`
// cookie on login which is the source of truth across page reloads; the
// in-memory copy is just convenience so axios can add a Bearer header when
// one is available (useful for cross-origin / non-cookie callers).
//
// We deliberately do NOT persist to localStorage: storing JWTs there exposes
// them to any XSS on the page, and the HttpOnly cookie already survives
// reloads.
let authToken: string | null = null;

export function setAuthToken(token: string | null) {
  authToken = token;
  // Clean up any token left behind by older builds that persisted to
  // localStorage so it cannot be exfiltrated by future XSS.
  try { localStorage.removeItem('auth_token'); } catch { /* ignore */ }
}

export function getAuthToken(): string | null {
  return authToken;
}

export function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export function clearAuthToken() {
  authToken = null;
  try { localStorage.removeItem('auth_token'); } catch { /* ignore */ }
}

// Callback for 401 errors — AuthContext registers a handler to clear user state
// without causing a full page reload.
let _onUnauthorized: (() => void) | null = null;
export function setOnUnauthorized(handler: (() => void) | null) {
  _onUnauthorized = handler;
}

// Callback for stale-JWT 403s — AuthContext registers a handler that re-selects
// the current tenant to get a fresh JWT with the up-to-date DB role, then returns
// the new access token so the interceptor can retry the original request once.
let _onTokenRefresh: (() => Promise<string | null>) | null = null;
export function setOnTokenRefresh(handler: (() => Promise<string | null>) | null) {
  _onTokenRefresh = handler;
}

// Deduplicates concurrent refresh calls so only one select-tenant round-trip fires.
let _refreshPromise: Promise<string | null> | null = null;
let _cookieSessionCheckPromise: Promise<boolean> | null = null;

// CSRF token — prefer in-memory value (set from login response body),
// fall back to reading the cookie (works when API is same-origin).
let _csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
  _csrfToken = token;
}

function getCsrfToken(): string | null {
  if (_csrfToken) return _csrfToken;
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Request interceptor - add auth token, CSRF token, and timing metadata
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Attach CSRF token on mutations
    const method = (config.method || '').toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrf = getCsrfToken();
      if (csrf) {
        config.headers['X-CSRF-Token'] = csrf;
      }
    }
    (config as any)._requestStartMs = performance.now();
    console.log(`[API] ➡️  ${config.method?.toUpperCase()} ${config.url}`, { params: config.params, timestamp: new Date().toISOString() });
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors and log timing
api.interceptors.response.use(
  (response) => {
    const startMs = (response.config as any)._requestStartMs as number | undefined;
    const elapsed = startMs != null ? (performance.now() - startMs).toFixed(1) : '?';
    const itemCount = Array.isArray(response.data) ? response.data.length
      : Array.isArray(response.data?.items) ? response.data.items.length
      : undefined;
    console.log(
      `[API] ✅  ${response.config.method?.toUpperCase()} ${response.config.url} — ${response.status} in ${elapsed}ms`,
      itemCount != null ? { items: itemCount, timestamp: new Date().toISOString() } : { timestamp: new Date().toISOString() },
    );
    return response;
  },
  async (error: AxiosError) => {
    const startMs = (error.config as any)?._requestStartMs as number | undefined;
    const elapsed = startMs != null ? (performance.now() - startMs).toFixed(1) : '?';
    console.error(
      `[API] ❌  ${error.config?.method?.toUpperCase()} ${error.config?.url} — ${error.response?.status ?? 'network error'} in ${elapsed}ms`,
      { timestamp: new Date().toISOString() },
    );
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isAuthRequest = requestUrl.includes('/auth/');
      const alreadyRetriedCookie = (error.config as any)?._retriedCookieSession === true;

      // A stale in-memory Bearer token can override a still-valid HttpOnly
      // auth_token cookie. Before logging the user out, drop the header,
      // verify the cookie-backed session with /auth/me, and retry once.
      if (!isAuthRequest && !alreadyRetriedCookie && error.config) {
        clearAuthToken();
        try {
          if (!_cookieSessionCheckPromise) {
            _cookieSessionCheckPromise = api
              .get('/auth/me', { _skipUnauthorizedHandler: true } as any)
              .then(() => true)
              .catch(() => false)
              .finally(() => { _cookieSessionCheckPromise = null; });
          }
          const cookieSessionValid = await _cookieSessionCheckPromise;
          if (cookieSessionValid) {
            (error.config as any)._retriedCookieSession = true;
            error.config.headers = error.config.headers ?? {};
            delete (error.config.headers as any).Authorization;
            return api.request(error.config);
          }
        } catch {
          // Fall through to the normal logout path below.
        }
      }

      clearAuthToken();
      if (_onUnauthorized && !(error.config as any)?._skipUnauthorizedHandler) {
        _onUnauthorized();
      }
    }
    // On role-related 403, silently refresh the JWT via select-tenant and retry once.
    // This handles stale-JWT scenarios where the in-memory token has an outdated role.
    if (
      error.response?.status === 403 &&
      !(error.config as any)?._retried &&
      _onTokenRefresh
    ) {
      const detail: string = (error.response.data as any)?.detail ?? '';
      const isRoleError = detail.startsWith('Requires ') || detail === 'Not a member of this tenant';
      if (isRoleError) {
        try {
          if (!_refreshPromise) {
            _refreshPromise = _onTokenRefresh().finally(() => { _refreshPromise = null; });
          }
          const newToken = await _refreshPromise;
          if (newToken && error.config) {
            (error.config as any)._retried = true;
            error.config.headers = error.config.headers ?? {};
            error.config.headers['Authorization'] = `Bearer ${newToken}`;
            return api.request(error.config);
          }
        } catch {
          // Refresh failed — fall through and reject with the original error
        }
      }
    }
    // Attach a user-friendly message for rate-limit responses
    if (error.response?.status === 429) {
      (error as any).rateLimited = true;
      if (!(error.response.data as any)?.detail) {
        error.response.data = { detail: 'The accounting service is temporarily busy. Please try again in a few seconds.' };
      }
    }
    return Promise.reject(error);
  }
);

// ==================
// Auth API
// ==================

export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  tenant_id?: string | null;
  role?: string | null;
  csrf_token?: string | null;
}

export interface AuthResponse {
  access_token: string;
  user: User;
  csrf_token?: string;
}

export interface Tenant {
  id: string;
  name: string;
  owner_id: string;
  role: string;
  created_at: string;
  updated_at: string;
}

export interface TenantMember {
  user_id: string;
  email?: string;
  name?: string;
  picture?: string;
  role: string;
  status: string;
  joined_at?: string;
  invited_by?: string;
  phone_number?: string;
  whatsapp_status?: 'pending' | 'verified' | null;
  assigned_customer_ids?: number[];
  ae_query_scope?: 'assigned' | 'all';
  ae_whatsapp_notifications_enabled?: boolean;
  customer_reminder_notifications_enabled?: boolean;
}

export interface SelectTenantResponse {
  access_token: string;
  tenant_id: string;
  role: string;
  csrf_token?: string;
}

export type ModuleId = 'core' | 'collections' | 'analytics' | 'expenses' | 'orders' | 'ai_agent';

export interface TenantModule {
  id: ModuleId;
  name: string;
  description: string;
  always_on: boolean;
  icon: string;
  enabled: boolean;
  enabled_at?: string | null;
  disabled_at?: string | null;
}

export interface TenantModulesResponse {
  modules: TenantModule[];
}

export async function googleAuth(googleToken: string): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/google', { token: googleToken });
  return response.data;
}

export async function testLogin(username: string, password: string): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/test-login', { username, password });
  return response.data;
}

export async function getCurrentUser(): Promise<User> {
  const response = await api.get<User>('/auth/me');
  return response.data;
}

export async function logout(): Promise<void> {
  await api.post('/auth/logout');
  clearAuthToken();
}

export const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true';
export const GOOGLE_AUTH_CONFIGURED = Boolean(import.meta.env.VITE_GOOGLE_CLIENT_ID);
export const TEST_LOGIN_ENABLED = import.meta.env.VITE_AUTH_TEST_LOGIN_ENABLED === 'true';

export async function devLogin(): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/dev-login');
  return response.data;
}

// ==================
// Tenants
// ==================
export async function listTenants(): Promise<{ tenants: Tenant[] }> {
  const response = await api.get<{ tenants: Tenant[] }>('/tenants');
  return response.data;
}

export async function createTenant(name: string): Promise<Tenant> {
  const response = await api.post<Tenant>('/tenants', { name });
  return response.data;
}

export async function getTenant(tenantId: string): Promise<Tenant> {
  const response = await api.get<Tenant>(`/tenants/${tenantId}`);
  return response.data;
}

export async function updateTenant(tenantId: string, data: { name?: string }): Promise<Tenant> {
  const response = await api.patch<Tenant>(`/tenants/${tenantId}`, data);
  return response.data;
}

export async function selectTenant(tenantId: string): Promise<SelectTenantResponse> {
  const response = await api.post<SelectTenantResponse>('/auth/select-tenant', { tenant_id: tenantId });
  return response.data;
}

export async function inviteMember(
  tenantId: string,
  role: string = 'member',
  options?: {
    name?: string;
    email?: string;
    phone_number?: string;
    assigned_customer_ids?: number[];
    ae_query_scope?: 'assigned' | 'all';
    ae_whatsapp_notifications_enabled?: boolean;
    customer_reminder_notifications_enabled?: boolean;
  },
): Promise<TenantMember> {
  const response = await api.post<TenantMember>(`/tenants/${tenantId}/invite`, { role, ...options });
  return response.data;
}

export async function listMembers(tenantId: string): Promise<{ members: TenantMember[] }> {
  const response = await api.get<{ members: TenantMember[] }>(`/tenants/${tenantId}/members`);
  return response.data;
}

export async function updateMemberRole(
  tenantId: string,
  userId: string,
  roleOrData: string | {
    role?: string;
    name?: string;
    email?: string;
    phone_number?: string;
    assigned_customer_ids?: number[];
    ae_query_scope?: 'assigned' | 'all';
    ae_whatsapp_notifications_enabled?: boolean;
    customer_reminder_notifications_enabled?: boolean;
  },
): Promise<TenantMember> {
  const data = typeof roleOrData === 'string' ? { role: roleOrData } : roleOrData;
  const response = await api.patch<TenantMember>(`/tenants/${tenantId}/members/${userId}`, data);
  return response.data;
}

export async function removeMember(tenantId: string, userId: string): Promise<void> {
  await api.delete(`/tenants/${tenantId}/members/${userId}`);
}

export async function approveAeNotifications(tenantId: string, userId: string): Promise<TenantMember> {
  const response = await api.post<TenantMember>(`/tenants/${tenantId}/members/${encodeURIComponent(userId)}/approve-notifications`);
  return response.data;
}

// ==================
// Customer Assignments
// ==================
export interface CustomerAssignedMember {
  user_id: string;
  email?: string | null;
  name?: string | null;
  picture?: string | null;
  role: string;
}

export interface CustomerAssignmentsResponse {
  customer_id: number;
  assigned_members: CustomerAssignedMember[];
}

export async function getCustomerAssignments(customerId: number): Promise<CustomerAssignmentsResponse> {
  const response = await api.get<CustomerAssignmentsResponse>(`/customers/${customerId}/assignments`);
  return response.data;
}

export async function setCustomerAssignments(customerId: number, memberUserIds: string[]): Promise<CustomerAssignmentsResponse> {
  const response = await api.put<CustomerAssignmentsResponse>(`/customers/${customerId}/assignments`, { member_user_ids: memberUserIds });
  return response.data;
}

// ==================
// Overview
// ==================
export const getOverview = () => api.get('/overview');

// ==================
// Chats
// ==================
export const listChats = (monitoredOnly: boolean = true) => api.get('/chats', { params: { monitored_only: monitoredOnly } });
export const getChat = (chatId: string) => api.get(`/chats/${chatId}`);
export const createChat = (data: any) => api.post('/chats', data);
export const updateChat = (chatId: string, data: any) => api.patch(`/chats/${chatId}`, data);
export const deleteChat = (chatId: string) => api.delete(`/chats/${chatId}`);
export const discoverChats = (chats: any[]) => api.post('/chats/discover', chats);
export const getChatSignals = (chatId: string) => api.get(`/chats/${chatId}/signals`);
export const getChatMessages = (chatId: string, params?: any) => api.get(`/chats/${chatId}/messages`, { params });

// ==================
// Messages
// ==================
export const ingestMessages = (data: any) => api.post('/messages/ingest', data);
export const listMessagesByChat = (chatId: string, params?: any) => api.get(`/messages/${chatId}`, { params });
export const getMessage = (messageId: string) => api.get(`/messages/detail/${messageId}`);

// ==================
// Signals
// ==================
export const listSignals = (params?: any) => api.get('/signals', { params });
export const getSignal = (signalId: string) => api.get(`/signals/${signalId}`);
export const updateSignal = (signalId: string, data: any) => api.patch(`/signals/${signalId}`, data);
export const confirmSignal = (signalId: string) => api.post(`/signals/${signalId}/confirm`);
export const dismissSignal = (signalId: string) => api.post(`/signals/${signalId}/dismiss`);
export const convertSignalToReminder = (signalId: string) => api.post(`/signals/${signalId}/convert-reminder`);
export const convertSignalToEntity = (signalId: string) => api.post(`/signals/${signalId}/convert-entity`);
export const bulkSignalAction = (data: any) => api.post('/signals/bulk', data);

// ==================
// Reminders
// ==================
export const listReminders = (params?: any) => api.get('/reminders', { params });
export const getReminder = (reminderId: string) => api.get(`/reminders/${reminderId}`);
export const createReminder = (data: any) => api.post('/reminders', data);
export const updateReminder = (reminderId: string, data: any) => api.patch(`/reminders/${reminderId}`, data);
export const snoozeReminder = (reminderId: string, data: any) => api.post(`/reminders/${reminderId}/snooze`, data);
export const completeReminder = (reminderId: string) => api.post(`/reminders/${reminderId}/complete`);
export const dismissReminder = (reminderId: string) => api.post(`/reminders/${reminderId}/dismiss`);

export interface WorkCustomer {
  customer_id: string;
  provider?: string;
  customer_name: string;
}
export type ActivityType = 'visit' | 'machine_replacement' | 'maintenance' | 'phone_call' | 'note';
export interface CustomerActivity extends WorkCustomer {
  activity_id: string;
  activity_type: ActivityType;
  occurred_at: string;
  notes: string;
  author_name: string;
  created_at: string;
}
export interface WorkReminder extends Partial<WorkCustomer> {
  id: string;
  title: string;
  description?: string;
  due_date?: string;
  effective_due_date?: string;
  status: string;
  version: number;
  priority: 'low' | 'medium' | 'high' | 'critical';
  notify_assigned_aes?: boolean;
  notification_recipient_user_ids?: string[];
  notification_status?: string;
  notification_error?: string;
  assigned_aes?: Array<{ user_id: string; name: string }>;
}
export interface WorkPage<T> { items: T[]; next_cursor?: string | null }
export interface ReminderRecipient {
  user_id: string; name: string; role?: string | null; assigned_ae?: boolean;
  eligible: boolean; reason?: string | null;
}
export interface ReminderNotification {
  recipients: ReminderRecipient[];
  attempts: Array<{ name: string; status: string; error?: string; created_at: string }>;
}
export const operationalCustomers = (params?: { q?: string; cursor?: string; limit?: number }) =>
  api.get<WorkPage<WorkCustomer>>('/customers/activity-directory', { params });
export const customerActivities = (id: string, params?: { provider?: string; cursor?: string }) =>
  api.get<WorkPage<CustomerActivity>>(`/customers/${encodeURIComponent(id)}/activities`, { params });
export const addCustomerActivity = (id: string, data: {
  activity_type: ActivityType; occurred_at: string; notes: string; request_id: string; provider?: string;
}) => api.post<CustomerActivity>(`/customers/${encodeURIComponent(id)}/activities`, data);
export const customerReminderRecipients = (id: string) =>
  api.get<ReminderRecipient[]>(`/customers/${encodeURIComponent(id)}/reminder-recipients`);
export const workReminderPage = (params: {
  customer_id?: string; provider?: string; assigned_ae?: string; bucket?: string; cursor?: string;
}) => api.get<WorkPage<WorkReminder>>('/reminders/page', { params });
export const workReminderAes = () => api.get<Array<{ user_id: string; name: string }>>('/reminders/assigned-aes');
export const workReminderNotification = (id: string) => api.get<ReminderNotification>(`/reminders/${id}/notification`);
export const retryReminderNotification = (id: string, version: number) =>
  api.post<WorkReminder>(`/reminders/${id}/retry-notification`, { version });

// ==================
// Entities
// ==================
export const listEntities = (params?: any) => api.get('/entities', { params });
export const getEntity = (entityId: string) => api.get(`/entities/${entityId}`);
export const createEntity = (data: any) => api.post('/entities', data);
export const updateEntity = (entityId: string, data: any) => api.patch(`/entities/${entityId}`, data);
export const linkSignalToEntity = (entityId: string, signalId: string) => api.post(`/entities/${entityId}/link-signal`, { signal_id: signalId });
export const mergeEntities = (data: any) => api.post('/entities/merge', data);

// ==================
// Settings
// ==================
export const getSettings = () => api.get('/settings');
export const updateSettings = (data: any) => api.patch('/settings', data);
export interface MessageTemplateCatalogEntry {
  key: string;
  label: string;
  description: string;
  channel: 'sms' | 'whatsapp' | 'sms_whatsapp' | 'multi_channel';
  template: string;
  default_template: string;
  allowed_placeholders: string[];
  required_placeholders: string[];
  sample_values: Record<string, string>;
  is_customized: boolean;
  source: 'override' | 'legacy' | 'environment' | 'default';
  twilio_content_managed: boolean;
}

export interface MessageTemplatePreview {
  template_key: string;
  body: string;
  warnings: string[];
}

export const getMessageTemplates = () =>
  api.get<{ items: MessageTemplateCatalogEntry[] }>('/settings/message-templates');
export const updateMessageTemplate = (templateKey: string, template: string) =>
  api.put<MessageTemplateCatalogEntry>(`/settings/message-templates/${templateKey}`, { template });
export const previewMessageTemplate = (
  templateKey: string,
  template?: string,
  sampleValues?: Record<string, string>,
) => api.post<MessageTemplatePreview>(
  `/settings/message-templates/${templateKey}/preview`,
  { template, sample_values: sampleValues },
);
export const renderMessageTemplate = (
  templateKey: string,
  values: Record<string, string>,
) => api.post<MessageTemplatePreview>(
  `/settings/message-templates/${templateKey}/render`,
  { values },
);
export const resetMessageTemplate = (templateKey: string) =>
  api.delete<MessageTemplateCatalogEntry>(`/settings/message-templates/${templateKey}`);
export const getModules = () => api.get<TenantModulesResponse>('/modules');
export const updateModule = (moduleId: ModuleId, enabled: boolean) =>
  api.put<TenantModule>(`/modules/${moduleId}`, { enabled });
export const getTrackedItemSettings = () =>
  api.get<{ items: Array<{ item_id: number; item_name?: string; catalog_number?: string; item_group: string; is_tracked: boolean; cost_price_nis?: number | null; blend_name?: string; is_coffee?: boolean }> }>('/settings/tracked-items');
export const setTrackedItemSetting = (
  itemId: number,
  data: { item_name?: string; catalog_number?: string; item_group?: string; is_tracked?: boolean; cost_price_nis?: number | null },
) => api.put('/settings/tracked-items/' + itemId, data);
export const deleteTrackedItemSetting = (itemId: number) => api.delete('/settings/tracked-items/' + itemId);
export const getChatRules = () => api.get('/settings/chat-rules');
export const setChatRules = (chatId: string, data: any) => api.put(`/settings/chat-rules/${chatId}`, data);
export const getRivhitConfig = () => api.get('/settings/rivhit');
export const updateRivhitConfig = (data: { api_token?: string; username?: string; password?: string; id_company?: string; payment_page_token?: string }) => api.put('/settings/rivhit', data);
export const testRivhitConfig = (data: { api_token?: string; username?: string; password?: string; id_company?: string; payment_page_token?: string }) => api.post('/settings/rivhit/test', data);
export const getRivhitSyncStatus = () => api.get('/settings/rivhit/sync-status');
export const resyncRivhitData = () => api.post('/settings/rivhit/resync');
export const getHeshbonitConfig = () => api.get('/settings/heshbonit-beclick');
export const updateHeshbonitConfig = (data: { username?: string; password?: string }) => api.put('/settings/heshbonit-beclick', data);
export const testHeshbonitConfig = (data: { username?: string; password?: string }) => api.post('/settings/heshbonit-beclick/test', data);
export const getHeshbonitSyncStatus = () => api.get('/settings/heshbonit-beclick/sync-status');
export const resyncHeshbonitData = () => api.post('/settings/heshbonit-beclick/resync');
export const getGreenInvoiceConfig = () => api.get('/settings/green-invoice');
export const updateGreenInvoiceConfig = (data: { api_key_id?: string; api_key_secret?: string; environment?: string; business_id?: string; base_url?: string }) =>
  api.put('/settings/green-invoice', data);
export const testGreenInvoiceConfig = (data: { api_key_id?: string; api_key_secret?: string; environment?: string; business_id?: string; base_url?: string }) =>
  api.post('/settings/green-invoice/test', data);
export const getGreenInvoiceSyncStatus = () => api.get('/settings/green-invoice/sync-status');
export const resyncGreenInvoiceData = () => api.post('/settings/green-invoice/resync');
export interface PermittedNumber {
  phone_number: string;
  status: string;
  pending_created_at?: string | null;
  verified_at?: string | null;
  daily_summary: boolean;
  notifications_enabled?: boolean;
  last_sent_at?: string | null;
  last_error?: string | null;
}

export interface DailySummarySchedule {
  send_hour: number;
  send_minute: number;
  timezone: string;
  send_days: number[];
}

export const getPermittedNumbers = () => api.get<{ numbers: PermittedNumber[] }>('/settings/permitted-numbers');
export const startPermittedNumberVerification = (data: { phone_number: string }) => api.post('/settings/permitted-numbers/start', data);
export const updatePermittedNumber = (phoneNumber: string, data: { daily_summary?: boolean; notifications_enabled?: boolean }) => api.patch(`/settings/permitted-numbers/${encodeURIComponent(phoneNumber)}`, data);
export const deletePermittedNumber = (phoneNumber: string) => api.delete(`/settings/permitted-numbers/${encodeURIComponent(phoneNumber)}`);
export const resendWaApproval = (phoneNumber: string) => api.post(`/settings/permitted-numbers/resend/${encodeURIComponent(phoneNumber)}`);
export const forceWaApproval = (phoneNumber: string) => api.post(`/settings/permitted-numbers/force-verify/${encodeURIComponent(phoneNumber)}`);
export const sendDailySummaryWhatsAppNow = () => api.post('/settings/daily-summary-whatsapp/send-now', {});
export const getDailySummarySchedule = () => api.get<DailySummarySchedule>('/settings/daily-summary-whatsapp/schedule');
export const updateDailySummarySchedule = (data: DailySummarySchedule) => api.put<DailySummarySchedule>('/settings/daily-summary-whatsapp/schedule', data);
export const sendBusinessAnalysisWhatsAppNow = () => api.post('/settings/business-analysis-whatsapp/send-now', {});
export const getBusinessAnalysisSchedule = () => api.get('/settings/business-analysis-whatsapp/schedule');
export const updateBusinessAnalysisSchedule = (data: { send_day: number; send_hour: number; send_minute: number; timezone: string }) => api.put('/settings/business-analysis-whatsapp/schedule', data);

// ==================
// Search
// ==================
export const globalSearch = (data: any) => api.post('/search', data);

// ==================
// Orders
// ==================
export const analyzeOrders = (data: { chat_id?: string; chat_ids?: string[]; hours: number }) => api.post('/orders/analyze', data);
export const extractCustomers = (data: { chat_id?: string; chat_ids?: string[]; hours: number }) => api.post('/orders/extract-customers', data);
export const extractItems = (data: { thread_id: string; confirmed_customers: Array<{ customer_id: string; customer_name: string; source_message_indices: number[] }> }) => api.post('/orders/extract-items', data);
export const listOrders = (params?: { chat_id?: string; status?: string; limit?: number }) => api.get('/orders', { params });
export const getOrder = (orderId: string) => api.get(`/orders/${orderId}`);
export const updateOrder = (orderId: string, data: any) => api.patch(`/orders/${orderId}`, data);
export const deleteOrder = (orderId: string) => api.delete(`/orders/${orderId}`);
export const reevaluateOrder = (orderId: string, data: { customer_id: string; customer_name: string }) => api.post(`/orders/${orderId}/reevaluate`, data);
export const getOrderSourceMessages = (orderId: string) => api.get(`/orders/${orderId}/source-messages`);
export const previewMessages = (chatId: string, hours: number) => api.get('/orders/messages-preview', { params: { chat_id: chatId, hours } });
export const getChatsWithMessages = () => api.get('/orders/chats-with-messages');
export const fetchWhatsAppMessages = (chatId: string) => api.post('/orders/fetch-whatsapp-messages', { chat_id: chatId, hours: 24 });
export const syncWhatsAppHistory = (chatId: string) => api.post('/orders/sync-history', { chat_id: chatId, hours: 24 });
export const resolvePrices = (data: { customer_id: string; items: Array<{ product_id: string; product_name?: string }> }) =>
  api.post<{ resolved: Array<{ product_id: string; product_name?: string; price?: number; price_source?: string; needs_manual_input: boolean; last_purchased_at?: string; catalog_price?: number }> }>('/orders/resolve-prices', data);
export const listOrderSuggestions = (limit: number = 10) => api.get('/orders/suggestions', { params: { limit } });
export const runOrderSuggestions = (chatId: string) => api.post('/orders/suggestions/run', { chat_id: chatId, hours: 2 });
export const dismissOrderSuggestion = (suggestionId: string) => api.post(`/orders/suggestions/${suggestionId}/dismiss`);
export const approveOrderSuggestion = (
  suggestionId: string,
  data: { document_type: number; document_number: number; document_link?: string; customer_id?: number | null }
) => api.post(`/orders/suggestions/${suggestionId}/approve`, data);

// ==================
// Customers
// ==================
export const listCustomers = (limit?: number) => api.get('/customers', { params: { limit } });
export const getCustomer = (customerId: string) => api.get(`/customers/${customerId}`);
export const searchCustomers = (q: string, limit?: number) => api.get('/customers/search', { params: { q, limit } });
export const getCustomerDebtReport = (customerId: number, params?: Record<string, string>) => api.get(`/reports/customers/${customerId}`, { params });

export type DebtReportLocale = 'en' | 'he';

export interface DebtReportLinkRequest {
  /** Report language. Defaults to Hebrew (`he`) server-side when omitted. */
  locale?: DebtReportLocale;
}

export interface DebtReportLinkResponse {
  customer_id: number;
  customer_name: string;
  locale: DebtReportLocale;
  /** Number of in-scope open invoices (0 for a real customer with none, or a supplier/payable account). */
  invoice_count: number;
  total_amount: number;
  /** Sum of remaining open balances — matches the report's "Remaining" total. */
  total_remaining: number;
  reconciliation?: CustomerReconciliation;
  /** Invoices shown as unverified and excluded from report totals. */
  excluded_unknown_count: number;
  from_date: string;
  to_date: string;
  generated_at: string;
  /** Time-limited presigned S3 URL for the generated PDF — treat as a bearer credential. */
  pdf_url: string;
  /** Compact app URL that resolves to the private PDF without extending its expiry. */
  short_url: string;
  /** Opaque identifier used to deliver this exact generated report to workspace members. */
  short_code: string;
  /** UTC timestamp when `pdf_url` stops working. */
  expires_at: string;
  /** Fixed at 259200 (72 hours). */
  expires_in_seconds: number;
}

/** Generates and publishes a private, time-limited (72h) debt-report PDF link for a customer. */
export const createCustomerDebtReportLink = (customerId: number, data?: DebtReportLinkRequest) =>
  api.post<DebtReportLinkResponse>(`/reports/customers/${customerId}/debt-report-link`, data || {});
export const downloadCustomerDebtReport = (customerId: number, shortCode: string) =>
  api.get<Blob>(`/reports/customers/${customerId}/debt-report-links/${shortCode}/download`, { responseType: 'blob' });

export interface DebtReportMemberDeliveryResult {
  user_id: string;
  status: 'sent' | 'failed' | 'skipped';
  reason?: string | null;
}

export interface DebtReportMemberSendResponse {
  status: 'sent' | 'partial' | 'failed';
  requested_count: number;
  sent_count: number;
  failed_count: number;
  skipped_count: number;
  results: DebtReportMemberDeliveryResult[];
}

export const sendDebtReportToMembers = (
  customerId: number,
  shortCode: string,
  memberUserIds: string[],
  deliveryMode: 'link' | 'attachment' = 'link',
) => api.post<DebtReportMemberSendResponse>(
  `/reports/customers/${customerId}/debt-report-links/${shortCode}/send-members`,
  { member_user_ids: memberUserIds, delivery_mode: deliveryMode },
);
export interface RivhitCustomerSettings {
  rivhit_customer_id: number;
  allow_message: boolean;
  payment_terms_days?: number | null;
  payment_terms_source?: string | null;
  payment_terms_updated_at?: string | null;
  rivhit_credit_terms?: number | null;
  rivhit_credit_days?: number | null;
}
export const getRivhitCustomerSettings = () =>
  api.get<RivhitCustomerSettings[]>('/customers/rivhit-settings');
export const syncRivhitCustomerPaymentTerms = () =>
  api.post<{ updated: number; skipped: number; settings: RivhitCustomerSettings[] }>('/customers/rivhit-settings/sync-payment-terms');
export const updateRivhitCustomerSettings = (customerId: number, data: { allow_message?: boolean; payment_terms_days?: number }) =>
  api.patch<RivhitCustomerSettings>(
    `/customers/rivhit/${customerId}/settings`,
    data,
  );

export const getCustomerInteractions = async (
  customerId: string,
  limit: number = 50,
  lastKey?: string
) => {
  const params: Record<string, any> = { limit };
  if (lastKey) params.last_key = lastKey;
  const response = await api.get(`/customers/${customerId}/interactions`, { params });
  return response.data;
};

export const getCustomerInteractionSummary = async (customerId: string) => {
  const response = await api.get(`/customers/${customerId}/interactions/summary`);
  return response.data;
};

export const logCustomerInteraction = (
  customerId: string | number,
  data: {
    channel: 'whatsapp' | 'email';
    message_type?: 'debt_report_sent' | 'payment_link_sent' | 'open_balance_sent' | 'document_sent';
    selected_action?: string;
    related_invoice_ids?: string[];
    related_payment_link_id?: string;
    message_text?: string;
    destination?: string;
    contact_id?: string;
    contact_name?: string;
    contact_role?: string;
    request_id?: string;
  },
) => api.post(`/customers/${customerId}/interactions`, data);

export interface CustomerContact {
  contact_id: string;
  name: string;
  role: string;
  phone: string;
}
export interface CustomerReconciliation {
  authoritative_balance: number | null;
  raw_open_total: number | null;
  effective_total: number | null;
  discrepancy: number | null;
  unknown_count: number;
  complete: boolean;
  documents: { document_type: number; document_number: number; document_date: string; document_link?: string; original_amount: number; raw_remaining: number | null; adjustment: number; effective_remaining: number | null }[];
}
export const getCustomerReconciliation = (customerId: number) =>
  api.get<CustomerReconciliation>(`/customers/${customerId}/reconciliation`);

export interface DebtReportInternalRecipient {
  user_id: string;
  name: string;
  role: string;
  phone_hint: string;
  assigned_to_customer: boolean;
  eligible?: boolean;
  blocked_reason?: 'inactive_membership' | 'accounting_access_missing' | 'customer_access_missing'
    | 'whatsapp_not_linked' | 'invalid_phone_number' | 'phone_ownership_mismatch'
    | 'phone_not_permitted' | 'customer_phone_number' | null;
}
export const getDebtReportRecipients = (customerId: number) =>
  api.get<DebtReportInternalRecipient[]>(`/reports/customers/${customerId}/debt-report-recipients`, {
    params: { include_unavailable: true },
  });
export interface CustomerContacts {
  provider: string;
  version: number;
  contacts: CustomerContact[];
}
export const getCustomerContacts = (customerId: number) =>
  api.get<CustomerContacts>(`/customers/${customerId}/contacts`);
export const saveCustomerContacts = (customerId: number, data: { version: number; contacts: CustomerContact[] }) =>
  api.put<CustomerContacts>(`/customers/${customerId}/contacts`, data);
export const sendDebtMessageSms = (customerId: number, data: { message_text: string; request_id: string }) =>
  api.post<{ status: 'accepted'; destination: string; history_saved: boolean; history_error?: string }>(
    `/customers/${customerId}/debt-message-sms`, data,
  );

// ==================
// Automations
// ==================
export type AutomationStatus = 'draft' | 'enabled' | 'disabled' | 'archived';
export type AutomationActionStatus = 'succeeded' | 'failed' | 'skipped';

export interface AutomationTrigger {
  type: 'weekly_schedule';
  day_of_week: string;
  time: string;
  timezone: string;
}

export interface AutomationCondition {
  type: 'customer_debt_age';
  debt_age_days: number;
  minimum_amount?: number;
}

export interface AutomationAction {
  type: 'send_customer_message';
  message_body: string;
  include_payment_link?: boolean;
  delivery_channel?: string | null;
}

export interface AutomationFlow {
  id: string;
  name: string;
  description?: string | null;
  status: AutomationStatus;
  trigger: AutomationTrigger;
  condition: AutomationCondition;
  action: AutomationAction;
  created_at: string;
  updated_at: string;
  enabled_at?: string | null;
  disabled_at?: string | null;
  last_run_at?: string | null;
  next_run_at?: string | null;
  version: number;
  trigger_summary?: string | null;
  condition_summary?: string | null;
  action_summary?: string | null;
}

export type AutomationFlowInput = Omit<AutomationFlow, 'id' | 'created_at' | 'updated_at' | 'version'>;

export interface AutomationPreviewCustomer {
  customer_id: string;
  customer_name: string;
  total_open_debt: number;
  aged_debt_amount: number;
  oldest_open_debt_days: number;
  phone?: string | null;
}

export interface AutomationPreview {
  flow_id?: string | null;
  matched_count: number;
  sample_customers: AutomationPreviewCustomer[];
  message_preview: string;
  generated_at: string;
  review_token: string;
}

export interface AutomationRun {
  id: string;
  flow_id: string;
  flow_version: number;
  status: string;
  triggered_by: string;
  started_at: string;
  finished_at?: string | null;
  matched_count: number;
  action_attempted_count: number;
  action_succeeded_count: number;
  action_failed_count: number;
  results: Array<{
    customer_id: string;
    customer_name: string;
    matched_reason: string;
    action_status: AutomationActionStatus;
    message_preview?: string | null;
    error_message?: string | null;
  }>;
  error_summary?: string | null;
}

export const listAutomations = (params?: { status?: string; q?: string; limit?: number }) =>
  api.get<{ items: AutomationFlow[] }>('/automations', { params });
export const createAutomation = (data: AutomationFlowInput) =>
  api.post<AutomationFlow>('/automations', data);
export const getAutomation = (automationId: string) =>
  api.get<AutomationFlow>(`/automations/${automationId}`);
export const updateAutomation = (automationId: string, data: Partial<AutomationFlowInput>) =>
  api.patch<AutomationFlow>(`/automations/${automationId}`, data);
export const deleteAutomation = (automationId: string) =>
  api.delete(`/automations/${automationId}`);
export const duplicateAutomation = (automationId: string) =>
  api.post<AutomationFlow>(`/automations/${automationId}/duplicate`);
export const previewAutomation = (data: { automation_id?: string; flow?: AutomationFlowInput }) =>
  api.post<AutomationPreview>('/automations/preview', data);
export const enableAutomation = (automationId: string, reviewToken: string) =>
  api.post<AutomationFlow>(`/automations/${automationId}/enable`, { review_token: reviewToken });
export const disableAutomation = (automationId: string) =>
  api.post<AutomationFlow>(`/automations/${automationId}/disable`);
export const runAutomation = (automationId: string) =>
  api.post<AutomationRun>(`/automations/${automationId}/run`);
export const listAutomationRuns = (automationId: string) =>
  api.get<{ items: AutomationRun[] }>(`/automations/${automationId}/runs`);
export const getAutomationRun = (automationId: string, runId: string) =>
  api.get<AutomationRun>(`/automations/${automationId}/runs/${runId}`);

// ==================
// Notifications
// ==================
export type NotificationRuleStatus = 'draft' | 'enabled' | 'disabled' | 'archived';

export type FilterOperator = '>=' | '<=' | '==' | '>' | '<' | '!=';

export interface FilterCondition {
  field: string;
  operator: FilterOperator;
  value: number;
}

export interface NotificationCondition {
  event_type: 'invoice_produced';
  debt_age_days: number;
  minimum_debt_amount: number;
  filter_conditions?: FilterCondition[];
  filter_query?: string | null;
}

export interface NotificationMessageTemplate {
  title: string;
  body: string;
  action_line?: string | null;
}

export interface NotificationRule {
  id: string;
  tenant_id: string;
  name: string;
  description?: string | null;
  status: NotificationRuleStatus;
  interval: 'hourly';
  condition: NotificationCondition;
  message_template: NotificationMessageTemplate;
  ai_generated?: boolean;
  ai_prompt?: string | null;
  created_at: string;
  updated_at: string;
  enabled_at?: string | null;
  disabled_at?: string | null;
  last_run_at?: string | null;
  next_run_at?: string | null;
  version: number;
}

export type NotificationRuleInput = Omit<NotificationRule, 'id' | 'tenant_id' | 'created_at' | 'updated_at' | 'version'>;

export interface NotificationPreviewEvent {
  event_key: string;
  customer_id?: string | null;
  customer_name: string;
  invoice_number: string;
  invoice_amount: number;
  total_open_debt: number;
  aged_debt_amount: number;
  oldest_open_debt_days: number;
  invoice_date: string;
  message_preview: string;
}

export interface NotificationPreview {
  rule_id?: string | null;
  matched_count: number;
  recipient_count: number;
  sample_events: NotificationPreviewEvent[];
  message_preview: string;
  generated_at: string;
}

export interface NotificationRun {
  id: string;
  rule_id: string;
  tenant_id: string;
  rule_version: number;
  status: string;
  triggered_by: string;
  started_at: string;
  finished_at?: string | null;
  matched_count: number;
  skipped_duplicate_count: number;
  action_attempted_count: number;
  action_succeeded_count: number;
  action_failed_count: number;
  results: Array<{
    event_key: string;
    customer_name: string;
    invoice_number: string;
    matched_reason: string;
    action_status: string;
    recipient?: string | null;
    provider_message_id?: string | null;
    error_message?: string | null;
    message_preview?: string | null;
  }>;
  error_summary?: string | null;
}

export const listNotificationRules = (params?: { status?: string; q?: string }) =>
  api.get<{ items: NotificationRule[] }>('/notifications/rules', { params });
export const createNotificationRule = (data: NotificationRuleInput) =>
  api.post<NotificationRule>('/notifications/rules', data);
export const updateNotificationRule = (ruleId: string, data: Partial<NotificationRuleInput>) =>
  api.patch<NotificationRule>(`/notifications/rules/${ruleId}`, data);
export const deleteNotificationRule = (ruleId: string) =>
  api.delete(`/notifications/rules/${ruleId}`);
export const previewNotificationRule = (data: { rule_id?: string; rule?: NotificationRuleInput }) =>
  api.post<NotificationPreview>('/notifications/preview', data);
export const enableNotificationRule = (ruleId: string) =>
  api.post<NotificationRule>(`/notifications/rules/${ruleId}/enable`);
export const disableNotificationRule = (ruleId: string) =>
  api.post<NotificationRule>(`/notifications/rules/${ruleId}/disable`);
export const runNotificationRule = (ruleId: string) =>
  api.post<NotificationRun>(`/notifications/rules/${ruleId}/run`);
export const listNotificationRuns = (ruleId: string) =>
  api.get<{ items: NotificationRun[] }>(`/notifications/rules/${ruleId}/runs`);

// ==================
// Products
// ==================
export const listProducts = (params?: { category?: string; limit?: number }) => api.get('/products', { params });
export const getProduct = (productId: string) => api.get(`/products/${productId}`);
export const searchProducts = (q: string, limit?: number) => api.get('/products/search', { params: { q, limit } });

// ==================
// Order History
// ==================
export const getCustomerOrderProfile = (customerId: string) => api.get(`/order-history/${customerId}`);

// ==================
// Invoicing (Rivhit)
// ==================
export const getInvoicingSummary = (force = false) =>
  api.get('/invoicing/summary', {
    params: {
      ...(force && { force: true }),
    },
  });
export const getDocumentTypes = () => api.get('/invoicing/document-types');
export const getReceiptTypes = () => api.get('/invoicing/receipt-types');
export const getPaymentTypes = () => api.get('/invoicing/payment-types');
export const getRivhitCustomers = (force = false, pagination?: { offset?: number; limit?: number }) => 
  api.get('/invoicing/customers', { 
    params: {
      ...(force && { force: true }),
      ...(pagination?.offset !== undefined && { offset: pagination.offset }),
      ...(pagination?.limit !== undefined && { limit: pagination.limit }),
    } 
  });
export const searchRivhitCustomers = (q: string, limit = 30) =>
  api.get('/invoicing/customers/search', { params: { q, limit } });
export const createRivhitCustomer = (data: {
  last_name: string;
  first_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  street?: string;
  id_number?: number;
  vat_number?: number;
}) => api.post<{ customer_id: number }>('/invoicing/customers', data);
export interface UpdateRivhitCustomerResponse {
  customer_id: number;
  last_name?: string;
  first_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  street?: string;
  id_number?: number;
  vat_number?: number;
  price_list_id?: number;
  discount_percent?: number;
}
export const updateRivhitCustomer = (customerId: number, data: {
  last_name?: string;
  first_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  street?: string;
  id_number?: number;
  vat_number?: number;
}) => api.patch<UpdateRivhitCustomerResponse>(`/invoicing/customers/${customerId}`, data);
export const getRivhitItems = (force = false) =>
  api.get('/invoicing/items', {
    params: {
      ...(force && { force: true }),
    },
  });
export const getRivhitItemPricing = (itemId: number, customerId: number) =>
  api.get('/invoicing/items/' + itemId + '/pricing', { params: { customer_id: customerId } });
export const getCustomerLastInvoiceItemPrice = (customerId: number, itemId: number) =>
  api.get<{ price_nis: number | null; document_type: number | null; document_number: number | null; document_date: string | null }>(
    `/invoicing/customers/${customerId}/items/${itemId}/last-invoice-price`,
  );
export const getCustomerRecentItems = (() => {
  const inflight = new Map<number, Promise<{ data: { item_ids: number[] } }>>();
  return (customerId: number) => {
    const existing = inflight.get(customerId);
    if (existing) return existing;
    const promise = api
      .get<{ item_ids: number[] }>('/invoicing/customers/' + customerId + '/recent-items')
      .finally(() => inflight.delete(customerId));
    inflight.set(customerId, promise);
    return promise;
  };
})();
export const getVatRate = () => api.get('/invoicing/vat-rate');
export const createPaymentLink = (data: {
  customer_id: number;
  amount_nis?: number;
  invoices_to_close?: Array<{
    document_type: number;
    document_number: number;
    amount_close: number;
    invoice_total?: number;
    open_balance?: number;
    document_date?: string;
  }>;
  description?: string;
  redirect_url?: string;
  ipn_url?: string;
  ipn_failure_url?: string;
  order?: string;
  reference?: number;
  max_payments?: number;
  min_payments?: number;
  number_of_payments?: number;
}) =>
  api.post<{
    success: boolean;
    url: string;
    short_url?: string;
    payment_link_id?: string;
    invoice_payment_id?: string;
    private_sale_token?: string;
    public_sale_token?: string;
    status?: number;
    debug_message?: string;
  }>('/invoicing/payment-links', data);

export const sendPaymentLinkWhatsApp = (data: {
  customer_id: number;
  payment_link: string;
  amount_nis: number;
  invoices_to_close: Array<{
    document_type: number;
    document_number: number;
    amount_close: number;
    invoice_total?: number;
    open_balance?: number;
    document_date?: string;
  }>;
  description?: string;
}) =>
  api.post<{
    success: boolean;
    customer_name: string;
    phone: string;
    message_preview: string;
  }>('/invoicing/payment-links/send-whatsapp', data);

export type PaymentNotificationChannel = 'whatsapp' | 'sms' | 'sms_and_whatsapp';

export interface PaymentRequestInvoiceSnapshot {
  invoice_id: string;
  document_number: string;
  issue_date?: string;
  due_date?: string;
  open_amount: number;
  currency?: string;
  description?: string;
  status?: string;
}

export interface PaymentNotificationResponse {
  payment_request: {
    id: string;
    business_id: string;
    customer_id: string;
    invoice_ids: string[];
    total_amount: number;
    currency: string;
    public_payment_link: string;
    short_payment_link?: string | null;
    selected_invoice_ids: string[];
    channel: PaymentNotificationChannel;
    sms_status?: string | null;
    whatsapp_status?: string | null;
    viewed_at?: string | null;
    paid_at?: string | null;
    expires_at: string;
    revoked_at?: string | null;
    status: string;
  };
  delivery: Record<string, { status: string; provider_message_id?: string | null; attempt: number }>;
}

export const previewPaymentNotificationSms = (data: {
  business_id?: string;
  customer_id: string;
  invoice_ids: string[];
  invoice_snapshots: PaymentRequestInvoiceSnapshot[];
  include_whatsapp_handoff?: boolean;
}) =>
  api.post<{
    body: string;
    character_count: number;
    public_payment_link_preview_available: boolean;
    warnings: string[];
  }>('/payment-notifications/preview-sms', data);

export const sendPaymentNotification = (data: {
  business_id?: string;
  customer_id: string;
  invoice_ids: string[];
  invoice_snapshots: PaymentRequestInvoiceSnapshot[];
  channel: PaymentNotificationChannel;
  include_whatsapp_handoff?: boolean;
  reuse_active_request?: boolean;
}) => api.post<PaymentNotificationResponse>('/payment-notifications/send', data);

export const resendPaymentNotificationSms = (paymentRequestId: string) =>
  api.post(`/payment-notifications/${paymentRequestId}/resend-sms`);

export const revokePaymentRequest = (paymentRequestId: string) =>
  api.post(`/payment-notifications/${paymentRequestId}/revoke`);

export interface SmsSettings {
  sms_enabled: boolean;
  sms_sender_number?: string | null;
  sms_sender_id?: string | null;
  sms_messaging_service_sid?: string | null;
  sms_whatsapp_handoff_enabled: boolean;
  default_payment_request_expiration_days: number;
  sms_template?: string | null;
  is_configured: boolean;
}

export const getSmsSettings = () => api.get<SmsSettings>('/payment-notifications/settings/sms');

export const updateSmsSettings = (data: Partial<SmsSettings>) =>
  api.put<SmsSettings>('/payment-notifications/settings/sms', data);

export const getPublicPaymentRequest = (token: string) =>
  api.get(`/public/payment-requests/${token}`);

export const updatePublicPaymentSelection = (token: string, selected_invoice_ids: string[]) =>
  api.post(`/public/payment-requests/${token}/selection`, { selected_invoice_ids });

export const startPublicPayment = (token: string, data: { mode: 'selected' | 'all'; selected_invoice_ids?: string[] }) =>
  api.post(`/public/payment-requests/${token}/pay`, data);

export const auditPublicWhatsappHandoff = (token: string) =>
  api.post(`/public/payment-requests/${token}/whatsapp-handoff`);

export interface PublicMobileCheckout {
  checkout_id: string;
  business_name: string;
  amount_ils: number;
  currency: string;
  description: string;
  status: 'active' | 'payment_started' | 'paid' | 'expired' | 'revoked';
  expires_at: string;
  requires_payer_details: boolean;
}

export interface PublicMobileCheckoutPayResult {
  checkout_id: string;
  payment_url?: string | null;
  status: 'active' | 'payment_started' | 'paid' | 'expired' | 'revoked';
}

export const getPublicMobileCheckout = (token: string) =>
  api.get<PublicMobileCheckout>(`/public/mobile-collection-checkouts/${token}`);

export const startPublicMobileCheckoutPayment = (
  token: string,
  data: { name?: string; phone?: string },
) => api.post<PublicMobileCheckoutPayResult>(`/public/mobile-collection-checkouts/${token}/pay`, data);

export interface ShortLinkResponse {
  code: string;
  target_token?: string | null;
  target_url?: string | null;
}

/** Resolve a short-link code to its target token or URL. */
export const resolveShortLink = (code: string) =>
  api.get<ShortLinkResponse>(`/public/short-links/${code}`);

export const listInvoices = (params?: Record<string, unknown>) => api.get('/invoicing/documents', { params });
export const createInvoice = async (data: Record<string, unknown>) => {
  const response = await api.post('/invoicing/documents', data);
  console.log(
    `[RivhitDocs] createInvoice response type=${response.data?.document_type} ` +
    `number=${response.data?.document_number} link=${response.data?.document_link || 'NONE'} ` +
    `ts=${new Date().toISOString()}`
  );
  rememberRivhitDocumentLink(
    response.data?.document_type,
    response.data?.document_number,
    response.data?.document_link,
    'invoice',
  );
  markRivhitDocumentsDirty();
  return response;
};
export const listReceipts = (params?: Record<string, unknown>) => api.get('/invoicing/receipts', { params });
export const createReceipt = async (data: Record<string, unknown>) => {
  console.log('[RivhitDocs] createReceipt payload:', JSON.stringify(data));
  const response = await api.post('/invoicing/receipts', data);
  // When the response contains invoice allocations, the receipt fields are
  // nested under `response.data.receipt` instead of at the top level.
  const receiptData = response.data?.receipt ?? response.data;
  console.log(
    `[RivhitDocs] createReceipt response type=${receiptData?.document_type} ` +
    `number=${receiptData?.document_number} link=${receiptData?.document_link || 'NONE'} ` +
    `ts=${new Date().toISOString()}`
  );
  rememberRivhitDocumentLink(
    receiptData?.document_type,
    receiptData?.document_number,
    receiptData?.document_link,
    'receipt',
  );
  markRivhitDocumentsDirty();
  return response;
};

export interface CustomerOpenInvoice {
  document_type: number;
  document_number: number;
  document_date: string;
  document_time?: string;
  document_type_name: string;
  amount: number;
  paid_amount?: number | null;
  open_balance?: number | null;
  balance_status?: 'provider' | 'local_overlay' | 'closure_metadata' | 'unknown';
  customer_id?: number;
  customer_name: string;
}

export const getCustomerOpenInvoices = (customerId: number) =>
  api.get<CustomerOpenInvoice[]>(`/invoicing/customers/${customerId}/open-invoices`);

export const getCustomerOpenDeliveryNotes = (customerId: number) =>
  api.get(`/invoicing/customers/${customerId}/open-delivery-notes`);

export const getReceiptPdfLink = (receiptType: number, receiptNumber: number) =>
  api.get<{ document_link: string }>(`/invoicing/receipts/${receiptType}/${receiptNumber}/link`);

export const getDocumentPdfLink = (documentType: number, documentNumber: number) =>
  api.get<{ document_link: string }>(`/invoicing/documents/${documentType}/${documentNumber}/link`);

export const retryCloseInvoices = (data: Record<string, unknown>) =>
  api.post('/invoicing/receipts/retry-close', data);

// ==================
// Bank reconciliation
// ==================

export type BankReconciliationConfidenceLevel = 'high' | 'medium' | 'low';
export type BankReconciliationRunStatus = 'analyzing' | 'completed' | 'partial_success' | 'failed';
export type BankReconciliationSuggestionStatus =
  | 'pending' | 'review_required' | 'edited' | 'overridden' | 'ignored'
  | 'executing' | 'executed' | 'resolved' | 'partial_success' | 'failed' | 'stale';
export type BankReconciliationExecutionStatus =
  | 'completed' | 'partial_success' | 'failed' | 'stale' | 'skipped';

export interface BankReconciliationConfidence {
  level: BankReconciliationConfidenceLevel;
  score: number;
  reason: string;
  evidence: string[];
}

export interface NormalizedBankTransactionEvidence {
  transaction_id: string;
  transaction_fingerprint: string;
  source_row_number: number;
  transaction_date: string;
  value_date?: string | null;
  direction: 'credit' | 'debit';
  amount: number;
  currency: string;
  payer_name?: string | null;
  description?: string | null;
  reference?: string | null;
  bank_code?: string | null;
  branch_number?: string | null;
  account_number_masked?: string | null;
}

export interface BankReconciliationInvoiceCandidate {
  document_type: number;
  document_number: number;
  document_id?: string | null;
  document_date: string;
  due_date?: string | null;
  original_amount: number;
  open_balance: number;
  currency: string;
  reference?: string | null;
  match_score: number;
  match_reasons: string[];
}

export interface BankReconciliationCustomerCandidate {
  customer_id: string;
  customer_name: string;
  match_score: number;
  match_reasons: string[];
  invoices: BankReconciliationInvoiceCandidate[];
}

export interface BankReconciliationInvoiceAllocation {
  document_type: number;
  document_number: number;
  document_id?: string | null;
  document_date: string;
  due_date?: string | null;
  invoice_total: number;
  open_balance: number;
  amount: number;
  remaining_balance: number;
}

export interface BankReconciliationPaymentDetails {
  receipt_type: number;
  payment_type: number;
  amount: number;
  transfer_date: string;
  reference?: string | null;
  description?: string | null;
  bank_code?: string | null;
  branch_number?: string | null;
  bank_account_number?: string | null;
  comments?: string | null;
  send_mail: boolean;
  email_to?: string | null;
}

export interface BankReconciliationInvoiceExecutionResult {
  document_type: number;
  document_number: number;
  document_id?: string | null;
  open_balance_before?: number | null;
  allocated_amount: number;
  remaining_balance?: number | null;
  status: 'fully_closed' | 'partially_closed' | 'failed' | 'stale';
  error_code?: string | null;
  error_message?: string | null;
}

export interface BankReconciliationExecutionOutcome {
  status: BankReconciliationExecutionStatus;
  attempted_at: string;
  completed_at?: string | null;
  idempotency_key: string;
  executed_version: number;
  receipt_document_type?: number | null;
  receipt_document_number?: number | null;
  receipt_document_id?: string | null;
  receipt_document_link?: string | null;
  reused_existing_receipt: boolean;
  allocated_amount: number;
  unallocated_amount: number;
  invoice_results: BankReconciliationInvoiceExecutionResult[];
  error_code?: string | null;
  error_message?: string | null;
}

export interface BankReconciliationSuggestion {
  suggestion_id: string;
  version: number;
  status: BankReconciliationSuggestionStatus;
  transaction: NormalizedBankTransactionEvidence;
  customer_id?: string | null;
  customer_name?: string | null;
  customer_candidates: BankReconciliationCustomerCandidate[];
  receipt_matches?: BankReconciliationReceiptMatch[];
  customer_identity_confidence?: number | null;
  customer_confirmed_by?: string | null;
  receipt_resolution?: {
    receipt: BankReconciliationReceiptMatch;
    basis: 'amount_date_assumed' | 'grouped_amount_date_assumed';
    date_difference_days: number;
    matched_at: string;
    stale: boolean;
    group_id?: string | null;
    group_total?: number | null;
    group_suggestion_ids?: string[];
    group_transaction_occurrence_fingerprints?: string[];
  } | null;
  allocations: BankReconciliationInvoiceAllocation[];
  payment: BankReconciliationPaymentDetails;
  confidence: BankReconciliationConfidence;
  allocated_amount: number;
  unallocated_amount: number;
  requires_override: boolean;
  material_edit_made: boolean;
  execution?: BankReconciliationExecutionOutcome | null;
  created_at: string;
  updated_at: string;
}

export interface BankReconciliationReceiptMatch {
  receipt_id: string;
  document_type: number;
  document_number: number;
  customer_id: string;
  receipt_date: string;
  amount: number;
  currency: string;
  reference?: string | null;
  confirmed: boolean;
  reason: string;
}

export interface BankReconciliationCustomerIdentity {
  customer_id: string;
  customer_name: string;
}

export interface BankReconciliationInvoiceOptions {
  customer_id: string;
  customer_name: string;
  invoices: BankReconciliationInvoiceCandidate[];
  suggested_allocations: BankReconciliationInvoiceAllocation[];
}

export const getBankReconciliationCustomers = (runId: string) =>
  api.get<BankReconciliationCustomerIdentity[]>(`/reconciliation/runs/${runId}/customers`);

export const refreshBankReconciliationReceipts = (runId: string, version: number) =>
  api.post<{
    run: BankReconciliationRun;
    can_delete: boolean;
    updated_run_ids: string[];
    warnings?: string[];
  }>(
    `/reconciliation/runs/${runId}/refresh-receipts`, { version },
  );

export type BankReconciliationSuggestionReceiptRefreshResult =
  | 'resolved' | 'group_resolved' | 'possible_match' | 'not_found' | 'unchanged';

export const refreshBankReconciliationSuggestionReceipt = (
  runId: string,
  suggestionId: string,
  version: number,
  suggestionVersion: number,
) => api.post<{
  run: BankReconciliationRun;
  can_delete: boolean;
  suggestion_id: string;
  result: BankReconciliationSuggestionReceiptRefreshResult;
  updated_run_ids: string[];
  warnings?: string[];
}>(
  `/reconciliation/runs/${runId}/suggestions/${suggestionId}/refresh-receipt`,
  { version, suggestion_version: suggestionVersion },
);

export const getBankReconciliationInvoiceOptions = (
  runId: string, suggestionId: string, customerId: string,
) => api.get<BankReconciliationInvoiceOptions>(
  `/reconciliation/runs/${runId}/suggestions/${suggestionId}/invoice-options`,
  { params: { customer_id: customerId } },
);

export interface BankReconciliationRunSummary {
  imported_rows: number;
  incoming_payment_rows: number;
  ignored_debit_rows: number;
  ignored_other_rows: number;
  suggestion_count: number;
  high_confidence_count: number;
  medium_confidence_count: number;
  low_confidence_count: number;
  executed_count: number;
  resolved_count?: number;
  ignored_count: number;
  failed_count: number;
  incoming_total: number;
  suggested_total: number;
  matched_total: number;
  unmatched_total: number;
  executed_total: number;
  currency: string;
}

export interface BankReconciliationRunListItem {
  can_delete?: boolean;
  run_id: string;
  import_id?: string | null;
  import_part_number?: number | null;
  version: number;
  status: BankReconciliationRunStatus;
  original_filename: string;
  parser_type: 'csv' | 'xlsx' | 'israeli_dat';
  provider: string;
  created_by: string;
  uploaded_at: string;
  analysis_completed_at?: string | null;
  summary: BankReconciliationRunSummary;
}

export interface BankReconciliationRun extends BankReconciliationRunListItem {
  tenant_id: string;
  file_extension: 'csv' | 'xlsx' | 'dat';
  content_type?: string | null;
  file_size_bytes: number;
  file_sha256: string;
  analysis_started_at: string;
  suggestions: BankReconciliationSuggestion[];
  error_code?: string | null;
  error_message?: string | null;
}

export interface BankReconciliationSuggestionEditRequest {
  version: number;
  action?: 'update' | 'ignore' | 'restore';
  customer_id?: string;
  customer_name?: string;
  payment?: Partial<BankReconciliationPaymentDetails>;
  allocations?: BankReconciliationInvoiceAllocation[];
  reason?: string;
}

export interface BankReconciliationExecuteRequest {
  version: number;
  override?: boolean;
  override_reason?: string;
}

export const analyzeBankReconciliation = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<{ run: BankReconciliationRun; duplicate_run_id?: string | null }>(
    '/reconciliation/analyze',
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

export interface BankReconciliationImportPart {
  part_number: number;
  version: number;
  status: 'queued' | 'analyzing' | 'completed' | 'failed';
  payment_count: number;
  processed_payments: number;
  run_ids: string[];
  error_message?: string | null;
}

export interface BankReconciliationImportResponse {
  can_delete?: boolean;
  import_record: {
    import_id: string;
    version: number;
    status: 'admitting' | 'queued' | 'analyzing' | 'completed' | 'partial_success' | 'failed';
    original_filename: string;
    provider: string;
    created_at: string;
    total_payments: number;
    processed_payments: number;
    part_count: number;
    completed_parts: number;
    failed_parts: number;
    imported_rows: number;
    ignored_debit_rows: number;
    ignored_other_rows: number;
    incoming_total: number | string;
    error_message?: string | null;
  };
  parts: BankReconciliationImportPart[];
  next_part?: number | null;
  reused: boolean;
}

export interface BankReconciliationProgressiveSuggestion {
  source_position: number;
  part_number: number;
  run_id: string;
  run_version: number;
  run_status: BankReconciliationRunStatus;
  suggestion: BankReconciliationSuggestion;
}

export interface BankReconciliationProgressiveSuggestionPage {
  import_id: string;
  import_status: BankReconciliationImportResponse['import_record']['status'];
  total_payments: number;
  analyzed_payments: number;
  suggestions: BankReconciliationProgressiveSuggestion[];
  next_start?: number | null;
}

export const startBankReconciliationImport = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return api.post<BankReconciliationImportResponse>(
    '/reconciliation/imports', form, { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

export const getBankReconciliationImport = (importId: string, start = 1) =>
  api.get<BankReconciliationImportResponse>(`/reconciliation/imports/${importId}`, { params: { start } });

export const getBankReconciliationImportSuggestions = (
  importId: string,
  start = 1,
  limit = 100,
) => api.get<BankReconciliationProgressiveSuggestionPage>(
  `/reconciliation/imports/${importId}/suggestions`,
  { params: { start, limit } },
);

export const retryBankReconciliationImportPart = (importId: string, partNumber: number, version: number) =>
  api.post<BankReconciliationImportResponse>(
    `/reconciliation/imports/${importId}/parts/${partNumber}/retry`, { version },
  );

export const listBankReconciliationRuns = (params?: {
  status?: BankReconciliationRunStatus;
  limit?: number;
  cursor?: string;
}) => api.get<{ runs: BankReconciliationRunListItem[]; next_cursor?: string | null }>(
  '/reconciliation/runs',
  { params },
);

export const getBankReconciliationRun = (runId: string) =>
  api.get<{ run: BankReconciliationRun; can_delete: boolean }>(`/reconciliation/runs/${runId}`);

export interface BankReconciliationDeletion {
  deletion_id: string;
  target_id: string;
  target_kind: 'import' | 'run';
  version: number;
  status: 'queued' | 'deleting' | 'waiting_execution' | 'failed' | 'completed';
  deleted_records: number;
  error_message?: string | null;
}

export const deleteBankReconciliationImport = (id: string, version: number) =>
  api.delete<BankReconciliationDeletion>(`/reconciliation/imports/${id}`, { data: { version } });

export const deleteBankReconciliationRun = (id: string, version: number) =>
  api.delete<BankReconciliationDeletion>(`/reconciliation/runs/${id}`, { data: { version } });

export const getBankReconciliationDeletion = (id: string) =>
  api.get<BankReconciliationDeletion>(`/reconciliation/deletions/${id}`);

export const retryBankReconciliationDeletion = (id: string, version: number) =>
  api.post<BankReconciliationDeletion>(`/reconciliation/deletions/${id}/retry`, { version });

export const editBankReconciliationSuggestion = (
  runId: string,
  suggestionId: string,
  data: BankReconciliationSuggestionEditRequest,
) => api.patch<{
  run_id: string;
  run_version: number;
  suggestion: BankReconciliationSuggestion;
  warnings?: string[];
}>(
  `/reconciliation/runs/${runId}/suggestions/${suggestionId}`,
  data,
);

export const executeBankReconciliationSuggestion = async (
  runId: string,
  suggestionId: string,
  data: BankReconciliationExecuteRequest,
) => {
  const response = await api.post<{
    run_id: string;
    run_version: number;
    suggestion_id: string;
    suggestion_version: number;
    outcome: BankReconciliationExecutionOutcome;
    warnings?: string[];
  }>(`/reconciliation/runs/${runId}/suggestions/${suggestionId}/execute`, data);
  if (['completed', 'partial_success'].includes(response.data.outcome.status)) {
    rememberRivhitDocumentLink(
      response.data.outcome.receipt_document_type ?? 0,
      response.data.outcome.receipt_document_number ?? 0,
      response.data.outcome.receipt_document_link ?? undefined,
      'receipt',
    );
    markRivhitDocumentsDirty();
  }
  return response;
};

export const executeBankReconciliationBatch = async (
  runId: string,
  data: {
    run_version: number;
    suggestions: Array<{
      suggestion_id: string;
      version: number;
      override?: boolean;
      override_reason?: string;
    }>;
  },
) => {
  const response = await api.post<{
    run_id: string;
    run_version: number;
    status: 'completed' | 'partial_success' | 'failed';
    requested_count: number;
    completed_count: number;
    partial_success_count: number;
    failed_count: number;
    stale_count: number;
    skipped_count: number;
    results: Array<{
      suggestion_id: string;
      suggestion_version: number;
      outcome: BankReconciliationExecutionOutcome;
      warnings?: string[];
    }>;
  }>(`/reconciliation/runs/${runId}/execute`, data);
  if (response.data.results.some(({ outcome }) => ['completed', 'partial_success'].includes(outcome.status))) {
    response.data.results.forEach(({ outcome }) => {
      rememberRivhitDocumentLink(
        outcome.receipt_document_type ?? 0,
        outcome.receipt_document_number ?? 0,
        outcome.receipt_document_link ?? undefined,
        'receipt',
      );
    });
    markRivhitDocumentsDirty();
  }
  return response;
};

// Unified documents (all types combined)
export const listAllDocuments = (params?: {
  from_date?: string;
  to_date?: string;
  search?: string;
  customer_name?: string;
  customer_id?: number;
  category_filter?: string;
  open_only?: boolean;
  last_interaction_filter?: 'all' | 'range' | 'none';
  last_interaction_from?: string;
  last_interaction_to?: string;
  group_by?: string;
  include_children?: boolean;
  include_service_activity?: boolean;
  sync?: boolean;
  limit?: number;
  offset?: number;
}) => api.get('/invoicing/all-documents', { params });

export const getDocumentsSummary = (params?: {
  from_date?: string;
  to_date?: string;
  search?: string;
  customer_name?: string;
  customer_id?: number;
  category_filter?: string;
  open_only?: boolean;
  sync?: boolean;
}) => api.get<{
  total_invoices_amount: number;
  total_receipts_amount: number;
  total_invoice_receipts_amount: number;
  total_invoice_receipts_count: number;
  total_open_amount: number;
  total_open_invoices_count: number;
}>('/invoicing/all-documents/summary', { params });

export const getDocumentDetails = (documentType: number, documentNumber: number) =>
  api.get<{ document_type: number; document_number: number; items: Array<{ item_id?: number; description?: string; catalog_number?: string; quantity: number; price_nis?: number; total_nis?: number }>; subtotal?: number; vat_rate?: number; vat_amount?: number; total?: number }>(
    `/invoicing/documents/${documentType}/${documentNumber}/details`
  );

export const sendOpenBalanceWhatsApp = (
  customerId: number,
  data?: {
    include_invoice_block_notice?: boolean;
    include_payment_link?: boolean;
    payment_link?: string | null;
  },
) =>
  api.post<{
    success: boolean;
    customer_name: string;
    phone: string;
    total_open: number;
    months: Array<{ month: string; amount: number; is_overdue: boolean }>;
    message_preview: string;
    payment_link?: string;
  }>(`/invoicing/customers/${customerId}/send-open-balance-sms`, data ?? {});

export const startPaymentFlow = (
  customerId: number,
  data?: { include_invoice_block_notice?: boolean },
) =>
  api.post<{
    flow_id: string;
    invoices_count: number;
    total_amount: number;
    status: string;
  }>(`/invoicing/customers/${customerId}/start-payment-flow`, data ?? {});

export const sendDocumentWhatsApp = (data: {
  phone: string;
  customer_name: string;
  document_number: string;
  document_type_name: string;
  amount: string;
  document_url: string;
}) =>
  api.post<{
    success: boolean;
    phone: string;
    message_preview: string;
  }>('/invoicing/documents/send-whatsapp', data);

// ==================
// Reports
// ==================
export const getReport = (params?: { period?: string; from_date?: string; to_date?: string }) =>
  api.get('/reports', { params });

export const generateReport = (params?: { period?: string; from_date?: string; to_date?: string }) =>
  api.post('/reports/generate', null, { params });

export const backfillReportLineItems = () =>
  api.post('/reports/backfill-line-items');

export const getReportCustomerPresets = (): Promise<ReportCustomerPreset[]> =>
  api.get('/reports/customer-presets').then(res => res.data.items);

export const createReportCustomerPreset = (data: ReportCustomerPresetInput): Promise<ReportCustomerPreset> =>
  api.post('/reports/customer-presets', data).then(res => res.data);

export const updateReportCustomerPreset = (
  presetId: string,
  data: Partial<ReportCustomerPresetInput>,
): Promise<ReportCustomerPreset> =>
  api.patch(`/reports/customer-presets/${presetId}`, data).then(res => res.data);

export const deleteReportCustomerPreset = (presetId: string): Promise<void> =>
  api.delete(`/reports/customer-presets/${presetId}`).then(() => undefined);

// ── Payment Link Settings ───────────────────────────────────────────────

export const getPaymentLinkSettings = () =>
  api.get('/payment-links/settings/current').then(res => res.data);

export const updatePaymentLinkSettings = (data: Record<string, unknown>) =>
  api.put('/payment-links/settings/current', data).then(res => res.data);

// ── WhatsApp Consent ───────────────────────────────────────────────────

export const getWhatsAppConsentStatus = (customerId: string) =>
  api.get(`/whatsapp-consent/${customerId}`).then(res => res.data);

export const enableWhatsApp = (customerId: string) =>
  api.post(`/whatsapp-consent/${customerId}/enable`).then(res => res.data);

export const resendOptIn = (customerId: string) =>
  api.post(`/whatsapp-consent/${customerId}/resend-optin`).then(res => res.data);

export const manualApproveConsent = (customerId: string, data: { consentSource: string; note: string }) =>
  api.post(`/whatsapp-consent/${customerId}/manual-approve`, data).then(res => res.data);

export const getConsentAuditLog = (customerId: string) =>
  api.get(`/whatsapp-consent/${customerId}/audit`).then(res => res.data);

export const checkConsentForSend = (customerId: string) =>
  api.post('/whatsapp-consent/check', { customerId }).then(res => res.data);

// ── Collections (גבייה) ───────────────────────────────────────────────

export interface CollectionActivity {
  customer_id: number;
  timestamp: string;
  activity_id: string;
  activity_type: string;
  details?: Record<string, unknown>;
  comment?: string | null;
  created_by?: string;
}

export interface CollectionActivityFeedItem extends CollectionActivity {
  customer_name: string;
}

export interface CollectionActivityFeedResponse {
  items: CollectionActivityFeedItem[];
  next_cursor?: string | null;
}

export interface CollectionCustomerSummary {
  customer_id: number;
  customer_name: string;
  phone?: string | null;
  open_balance: number;
  aged_debt: number;
  oldest_unpaid_days: number;
  last_payment_date?: string | null;
  collection_score: number;
  tracked_item_growth_pct?: number | null;
  tracked_item_growth_status?: 'growing' | 'stable' | 'struggling' | null;
  last_activity?: CollectionActivity | null;
  active_payment_link?: { id: string; status: string; amount: number } | null;
  next_reminder?: { id: string; due_date: string } | null;
  automation_status?: string | null;
  assigned_members?: CustomerAssignedMember[];
  allow_message?: boolean;
}

export type CollectionPresetPurchaseTrend = 'growing' | 'stable' | 'struggling';

export interface CollectionPresetFilters {
  aged_debt_greater_than?: number | null;
  min_open_balance?: number | null;
  min_collection_score?: number | null;
  purchase_trend?: CollectionPresetPurchaseTrend | null;
  assigned_member_id?: string | null;
  unassigned_only?: boolean;
}

export interface CollectionPreset {
  id: string;
  tenant_id: string;
  name: string;
  filters: CollectionPresetFilters;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface CollectionPresetInput {
  name: string;
  filters: CollectionPresetFilters;
}

export interface CollectionKPIs {
  total_open_debt: number;
  aged_debt: number;
  overdue_amount: number;
  due_this_week: number;
  uncertain_open_invoices_count?: number;
  customers_requiring_followup: number;
  active_payment_links: number;
  paid_this_month?: number | null;
  customers_with_repeated_delays?: number | null;
  failed_or_ignored_whatsapp_messages?: number | null;
  payment_links_sent?: number | null;
  payment_links_paid?: number | null;
  payment_links_paid_rate?: number | null;
  last_calculated_at?: string | null;
  cache_status: 'available' | 'not_available';
  debt_trend: { month: string; total_debt: number; aged_debt: number }[];
}

export interface UserDebtSummary {
  user_id: string;
  email?: string | null;
  name?: string | null;
  total_open_debt: number;
  aged_debt: number;
  customer_count: number;
  paid_this_month?: number | null;
}

export interface CollectionTrendByUserPoint {
  month: string;
  user_id: string;
  email?: string | null;
  name?: string | null;
  collected_amount: number;
}

export interface DebtTrendByUserPoint {
  month: string;
  user_id: string;
  email?: string | null;
  name?: string | null;
  total_debt: number;
  aged_debt: number;
}

export interface CollectionDashboardResponse {
  kpis: CollectionKPIs;
  customers: CollectionCustomerSummary[];
  debt_by_user: UserDebtSummary[];
  collection_trend_by_user?: CollectionTrendByUserPoint[];
  debt_trend_by_user?: DebtTrendByUserPoint[];
}

export interface BulkCollectionActionResponse {
  total: number;
  succeeded: number;
  failed: number;
  results: { customer_id: number; success: boolean; error?: string }[];
}

export const getCollectionsDashboard = (fromDate?: string, toDate?: string): Promise<CollectionDashboardResponse> =>
  api.get('/collections/dashboard', { params: { from_date: fromDate, to_date: toDate }, timeout: 25000 }).then(res => res.data);

export const getCollectionPresets = (): Promise<CollectionPreset[]> =>
  api.get('/collections/presets').then(res => res.data.items);

export const createCollectionPreset = (data: CollectionPresetInput): Promise<CollectionPreset> =>
  api.post('/collections/presets', data).then(res => res.data);

export const updateCollectionPreset = (presetId: string, data: Partial<CollectionPresetInput>): Promise<CollectionPreset> =>
  api.patch(`/collections/presets/${presetId}`, data).then(res => res.data);

export const deleteCollectionPreset = (presetId: string): Promise<void> =>
  api.delete(`/collections/presets/${presetId}`).then(() => undefined);

export const recalculateCollectionKpis = (): Promise<CollectionKPIs> =>
  api.post('/collections/recalculate-kpis').then(res => res.data);

export const getCollectionActivities = (customerId: number, limit = 50): Promise<CollectionActivity[]> =>
  api.get(`/collections/activities/${customerId}`, { params: { limit } }).then(res => res.data);

export const getCollectionActivityFeed = (
  limit = 20,
  cursor?: string,
): Promise<CollectionActivityFeedResponse> =>
  api.get('/collections/activities', { params: { limit, cursor } }).then(res => res.data);

export const logCollectionActivity = (data: {
  customer_id: number;
  activity_type: string;
  details?: Record<string, unknown>;
  comment?: string;
}): Promise<CollectionActivity> =>
  api.post('/collections/activities', data).then(res => res.data);

export const bulkCollectionAction = (data: {
  customer_ids: number[];
  action_type: 'send_whatsapp' | 'create_payment_link';
  include_payment_link?: boolean;
  message_body?: string;
}): Promise<BulkCollectionActionResponse> =>
  api.post('/collections/bulk-action', data).then(res => res.data);

export type MessageUsageGroupBy = 'tenant' | 'user' | 'channel' | 'user_channel';

export interface MessageUsageRow {
  tenant_id: string;
  count: number;
  actor_user_id?: string | null;
  actor_label?: string | null;
  channel?: 'sms' | 'whatsapp' | null;
}

export interface MessageUsageSummaryResponse {
  tenant_id: string;
  from_date: string;
  to_date: string;
  group_by: MessageUsageGroupBy;
  total: number;
  channels: { sms: number; whatsapp: number };
  rows: MessageUsageRow[];
}

export const getMessageUsage = (
  from: string,
  to: string,
  groupBy: MessageUsageGroupBy = 'user_channel',
): Promise<MessageUsageSummaryResponse> =>
  api.get('/message-usage', { params: { from, to, group_by: groupBy } }).then(res => res.data);

export interface UserUsageFeatureBreakdown {
  feature: UserUsageFeature;
  page_views: number;
  actions: number;
}

export interface UserUsageUserSummary {
  user_id: string;
  name: string;
  email: string;
  role: string;
  last_active_at?: string | null;
  active_days: number;
  page_views: number;
  actions: number;
  features: UserUsageFeatureBreakdown[];
}

export interface UserUsageDailyPoint {
  date: string;
  active_users: number;
  page_views: number;
  actions: number;
}

export interface UserUsageSummaryResponse {
  tenant_id: string;
  from_date: string;
  to_date: string;
  timezone: string;
  totals: {
    active_members: number;
    total_members: number;
    page_views: number;
    actions: number;
    average_active_days: number;
  };
  daily: UserUsageDailyPoint[];
  users: UserUsageUserSummary[];
}

export const trackUserUsagePageView = (feature: UserUsageFeature): Promise<void> =>
  api.post('/user-usage/page-view', { feature }).then(() => undefined);

export const getUserUsage = (from: string, to: string): Promise<UserUsageSummaryResponse> =>
  api.get('/user-usage', { params: { from, to } }).then(res => res.data);

export interface McpUsageCounts {
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
}

export interface McpUsageSummaryResponse {
  tenant_id: string;
  from_date: string;
  to_date: string;
  timezone: string;
  totals: McpUsageCounts;
  daily: (McpUsageCounts & { date: string })[];
  tools: (McpUsageCounts & { tool_name: string })[];
  principals: (McpUsageCounts & {
    principal_id: string;
    user_id: string | null;
    name: string | null;
    last_active_at: string | null;
  })[];
}

export const getMcpUsage = (from: string, to: string, signal?: AbortSignal): Promise<McpUsageSummaryResponse> =>
  api.get('/user-usage/mcp', { params: { from, to }, signal }).then(res => res.data);

// ── Workspace bug reports ───────────────────────────────────────────────────

export type BugSeverity = 'low' | 'medium' | 'high' | 'critical';
export type BugImageContentType = 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif';

export interface BugViewport {
  width: number;
  height: number;
  device_pixel_ratio?: number | null;
}

export interface BugClientContext {
  route: string;
  page_title?: string | null;
  locale: string;
  user_agent: string;
  viewport?: BugViewport | null;
}

export interface BugAttachmentReference {
  report_id: string;
  attachment_id: string;
}

export interface BugAttachmentUploadRequest {
  file_name: string;
  content_type: BugImageContentType;
  size_bytes: number;
}

export interface BugAttachmentUploadResponse {
  attachment: BugAttachmentReference;
  upload_url: string;
  upload_fields: Record<string, string>;
  expires_at: string;
}

export interface BugAttachmentMetadata {
  id: string;
  file_name: string;
  content_type: BugImageContentType;
  size_bytes: number;
  read_url: string;
  read_url_expires_at: string;
}

export interface BugReporter {
  id: string;
  name: string;
  email?: string | null;
}

export interface BugReportCreate {
  title: string;
  description: string;
  severity: BugSeverity;
  client_context: BugClientContext;
  attachment?: BugAttachmentReference | null;
}

export interface BugReport {
  id: string;
  title: string;
  description: string;
  severity: BugSeverity;
  client_context: BugClientContext;
  reporter: BugReporter;
  attachment?: BugAttachmentMetadata | null;
  created_at: string;
}

export interface BugReportListResponse {
  items: BugReport[];
  next_cursor?: string | null;
}

export const createBugAttachmentUpload = (
  data: BugAttachmentUploadRequest,
): Promise<BugAttachmentUploadResponse> =>
  api.post<BugAttachmentUploadResponse>('/bugs/attachment-upload', data).then(res => res.data);

export async function uploadBugAttachmentFile(
  authorization: BugAttachmentUploadResponse,
  file: File,
): Promise<void> {
  const body = new FormData();
  Object.entries(authorization.upload_fields).forEach(([name, value]) => {
    body.append(name, value);
  });
  body.append('file', file);
  const response = await fetch(authorization.upload_url, {
    method: 'POST',
    body,
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`Attachment upload failed with status ${response.status}`);
  }
}

export const createBugReport = (data: BugReportCreate): Promise<BugReport> =>
  api.post<BugReport>('/bugs', data).then(res => res.data);

export const listBugReports = (
  limit = 20,
  cursor?: string | null,
): Promise<BugReportListResponse> =>
  api.get<BugReportListResponse>('/bugs', {
    params: { limit, ...(cursor ? { cursor } : {}) },
  }).then(res => res.data);

export const getBugReport = (reportId: string): Promise<BugReport> =>
  api.get<BugReport>(`/bugs/${encodeURIComponent(reportId)}`).then(res => res.data);

export const deleteBugReport = (reportId: string): Promise<void> =>
  api.delete(`/bugs/${encodeURIComponent(reportId)}`).then(() => undefined);

// ── Simulator ──────────────────────────────────────────────────────────

export interface SimulatorMessageRequest {
  phone: string;
  text: string;
  is_card_reply?: boolean;
  callback_data?: string | null;
  sender_name?: string;
}

export interface SimulatorOutboundMessage {
  type: 'text' | 'card';
  chat_id: string;
  text?: string;
  rendered?: {
    supported: boolean;
    body: string;
    parser_hint?: string;
    reply_markup?: {
      twilio_content?: {
        content_sid?: string;
        content_variables?: Record<string, string>;
      };
    };
  };
  card_data?: any;
}

export const simulateMessage = (data: SimulatorMessageRequest) =>
  api.post<{ outbound: SimulatorOutboundMessage[] }>('/simulator/message', data).then(res => res.data);

export const resetSimulatorSession = (phone: string) =>
  api.delete('/simulator/session', { data: { phone } });

// ── MCP API Key ──────────────────────────────────────────────────────────────

export interface McpKeyStatus {
  has_key: boolean;
  key_prefix?: string;
  created_at?: string;
}

export interface McpKeyGenerateResponse {
  key: string;
  key_prefix: string;
  created_at: string;
}

export const getMcpKeyStatus = () =>
  api.get<McpKeyStatus>('/settings/mcp-key/status');

export const generateMcpKey = () =>
  api.post<McpKeyGenerateResponse>('/settings/mcp-key');

export const revokeMcpKey = () =>
  api.delete('/settings/mcp-key');

// ── Mobile collections payment links ──────────────────────────────────────────

export interface MobileCollectionTimelineEvent {
  event_id: string;
  timestamp: string;
  event_type: string;
  message: string;
  details: Record<string, unknown>;
}

export interface MobileCollectionTransaction {
  id: string;
  tenant_id: string;
  customer_name: string;
  customer_phone: string;
  customer_id?: string | null;
  amount_ils: number;
  description?: string | null;
  mode: 'link' | 'cash';
  status: 'pending' | 'paid_link' | 'paid_cash' | 'overdue';
  payment_link_id?: string | null;
  payment_link_url?: string | null;
  receipt_document_number?: number | null;
  reminders_enabled: boolean;
  reminders_sent?: number;
  next_reminder_at?: string | null;
  reminders_stopped?: boolean;
  last_reminder_at?: string | null;
  last_reminder_channel?: string | null;
  paid_at?: string | null;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
  timeline: MobileCollectionTimelineEvent[];
}

export const listMobileCollectionTransactions = (status?: string) =>
  api
    .get<{ items: MobileCollectionTransaction[] }>('/mobile-collections/transactions', {
      params: status ? { status } : undefined,
    })
    .then(res => res.data.items);

export const setMobileCollectionReminders = (id: string, enabled: boolean) =>
  api
    .put<MobileCollectionTransaction>(`/mobile-collections/transactions/${id}/reminders`, { enabled })
    .then(res => res.data);

export const sendMobileCollectionReminder = (id: string) =>
  api
    .post<MobileCollectionTransaction>(`/mobile-collections/transactions/${id}/send-reminder`, {})
    .then(res => res.data);

export default api;
